/**
 * 大屏地图动画相关的纯计算：小车平滑移动、当日轨迹回放、详情弹窗定位、换仓定位。
 * 不依赖 mapbox / DOM，便于单测；页面侧只负责把结果写到 marker 与样式上。
 */
import dayjs from 'dayjs';
import type {
    DeliveryMonitorDriverPositionType,
    DriverTrajectoryPointType,
} from '../../../graphql/types/deliveryMonitor/DeliveryMonitorTypes';
import { DASHBOARD_PACIFIC_TZ } from '../../../utils/dashboardPacificTime';
import { SiteType } from '../../../graphql/types/SiteType';

export interface MotionPoint {
    lng: number;
    lat: number;
    ts: number;
    bearing: number | null;
}

export const toMotionPoint = (p: DriverTrajectoryPointType): MotionPoint => ({
    lng: Number(p.longitude),
    lat: Number(p.latitude),
    // Long 经 GraphQL 序列化后运行时可能是字符串
    ts: Number(p.locationTimestampMs),
    bearing: p.bearing === null || p.bearing === undefined ? null : Number(p.bearing),
});

/**
 * 本轮滑行路径：起点为当前显示位置，后接 track 中比它新的点。
 * 没有新点返回 []，表示原地不动。
 */
export const buildGlidePath = (shown: MotionPoint, track: MotionPoint[]): MotionPoint[] => {
    const fresh = track.filter((p) => p.ts > shown.ts);
    return fresh.length === 0 ? [] : [shown, ...fresh];
};

export interface PathSample {
    point: MotionPoint;
    /** 当前所在线段的起点下标（线段为 path[segment] → path[segment + 1]） */
    segment: number;
}

/** 按时间进度插值：progress 0..1 线性映射到路径首尾 ts 区间 */
export const sampleByTime = (path: MotionPoint[], progress: number): PathSample => {
    const last = path.length - 1;
    const first = path[0];
    const span = path[last].ts - first.ts;
    if (last === 0 || span <= 0 || progress >= 1) {
        return { point: path[last], segment: Math.max(0, last - 1) };
    }
    const target = first.ts + Math.max(0, progress) * span;
    let i = 0;
    while (i < last - 1 && path[i + 1].ts <= target) i++;
    const a = path[i];
    const b = path[i + 1];
    const segSpan = b.ts - a.ts;
    const f = segSpan > 0 ? (target - a.ts) / segSpan : 1;
    return {
        point: {
            lng: a.lng + (b.lng - a.lng) * f,
            lat: a.lat + (b.lat - a.lat) * f,
            ts: target,
            bearing: b.bearing,
        },
        segment: i,
    };
};

// Web Mercator 归一化坐标（0..1），与地图屏幕方向一致：x 向东、y 向南
const mercatorX = (lng: number): number => (lng + 180) / 360;
const mercatorY = (lat: number): number => {
    const rad = (lat * Math.PI) / 180;
    return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2;
};

// 约 0.4m，两点更近时视为原地，不改朝向
const MIN_HEADING_DISTANCE = 1e-8;

/** a → b 的屏幕朝向（0 = 正北，顺时针）；两点几乎重合时返回 null */
export const headingDeg = (a: MotionPoint, b: MotionPoint): number | null => {
    const dx = mercatorX(b.lng) - mercatorX(a.lng);
    const dy = mercatorY(b.lat) - mercatorY(a.lat);
    if (Math.hypot(dx, dy) < MIN_HEADING_DISTANCE) return null;
    const deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
    return (deg + 360) % 360;
};

/** 滑行时的车头朝向：优先取线段终点上报的 bearing，缺失时用两点航向 */
export const segmentHeading = (path: MotionPoint[], segment: number): number | null => {
    const b = path[segment + 1];
    if (!b) return null;
    if (b.bearing !== null) return b.bearing;
    return headingDeg(path[segment], b);
};

/** 线段起止坐标不同即算在动（不设位移阈值）；没有下一个点返回 false */
export const segmentMoves = (path: MotionPoint[], segment: number): boolean => {
    const a = path[segment];
    const b = path[segment + 1];
    if (!a || !b) return false;
    return a.lng !== b.lng || a.lat !== b.lat;
};

/** 量化到 step 度并归一到 [0, 360) */
export const quantizeDeg = (deg: number, step: number): number =>
    (((Math.round(deg / step) * step) % 360) + 360) % 360;

/** 取与 prev 最接近的等价角度，避免 350° → 10° 时 CSS 过渡绕远路转一整圈 */
export const nearestEquivalentDeg = (prev: number | null, next: number): number => {
    if (prev === null) return next;
    const diff = ((((next - prev) % 360) + 540) % 360) - 180;
    return prev + diff;
};

export interface DistanceTable {
    /** cumulative[i] = 起点到 points[i] 的累计距离 */
    cumulative: number[];
    total: number;
}

/**
 * 按 Mercator 平面距离累计。与 mapbox `line-progress` 同一度量，回放车头和轨迹线头能对齐；
 * 城市尺度下与地理距离的差异可忽略。
 */
export const buildDistanceTable = (points: MotionPoint[]): DistanceTable => {
    const cumulative = [0];
    for (let i = 1; i < points.length; i++) {
        const dx = mercatorX(points[i].lng) - mercatorX(points[i - 1].lng);
        const dy = mercatorY(points[i].lat) - mercatorY(points[i - 1].lat);
        cumulative.push(cumulative[i - 1] + Math.hypot(dx, dy));
    }
    return { cumulative, total: cumulative[cumulative.length - 1] };
};

export interface DistanceSample {
    lng: number;
    lat: number;
    /** 当前所在线段的起点下标；只有 1 个点时为 0 */
    segment: number;
}

/** 按距离匀速：progress 0..1 对应已走过的距离占比 */
export const sampleByDistance = (
    points: MotionPoint[],
    table: DistanceTable,
    progress: number,
): DistanceSample => {
    const last = points.length - 1;
    if (last === 0 || table.total <= 0) {
        return { lng: points[0].lng, lat: points[0].lat, segment: 0 };
    }
    const p = Math.min(1, Math.max(0, progress));
    const want = p * table.total;
    let i = 0;
    while (i < last - 1 && table.cumulative[i + 1] < want) i++;
    const segLen = table.cumulative[i + 1] - table.cumulative[i];
    const f = segLen > 0 ? Math.min(1, (want - table.cumulative[i]) / segLen) : 1;
    const a = points[i];
    const b = points[i + 1];
    return {
        lng: a.lng + (b.lng - a.lng) * f,
        lat: a.lat + (b.lat - a.lat) * f,
        segment: i,
    };
};

// 弹窗贴车：车右侧 +26px、上方 -150px；右侧放不下翻到左侧（与 PRD positionPop 一致）
const POPUP_OFFSET_X = 26;
const POPUP_OFFSET_Y = -150;
const POPUP_FLIP_GAP = 18;
const POPUP_MARGIN = 8;

export interface PopupPlacementInput {
    /** 小车在地图容器内的像素坐标；null = 司机无位置，停靠在地图中部 */
    anchor: { x: number; y: number } | null;
    width: number;
    height: number;
    boundsWidth: number;
    boundsHeight: number;
}

export const placePopup = ({
    anchor,
    width,
    height,
    boundsWidth,
    boundsHeight,
}: PopupPlacementInput): { x: number; y: number } => {
    const maxX = Math.max(POPUP_MARGIN, boundsWidth - width - POPUP_MARGIN);
    const maxY = Math.max(POPUP_MARGIN, boundsHeight - height - POPUP_MARGIN);
    const clamp = (v: number, max: number) => Math.round(Math.max(POPUP_MARGIN, Math.min(v, max)));
    if (anchor === null) {
        return { x: clamp((boundsWidth - width) / 2, maxX), y: clamp((boundsHeight - height) / 2, maxY) };
    }
    let x = anchor.x + POPUP_OFFSET_X;
    if (x + width > boundsWidth - POPUP_MARGIN) {
        x = anchor.x - width - POPUP_FLIP_GAP;
    }
    return { x: clamp(x, maxX), y: clamp(anchor.y + POPUP_OFFSET_Y, maxY) };
};

export interface GeoBounds {
    west: number;
    east: number;
    south: number;
    north: number;
}

/** 点是否落在向内收缩 inset 比例后的视野内（PRD：pad(-.12)） */
export const containsWithInset = (b: GeoBounds, lng: number, lat: number, inset: number): boolean => {
    const dLng = (b.east - b.west) * inset;
    const dLat = (b.north - b.south) * inset;
    return lng >= b.west + dLng && lng <= b.east - dLng && lat >= b.south + dLat && lat <= b.north - dLat;
};

export interface WhsSite {
    id: string;
    longitude: number;
    latitude: number;
}

/** center = 以仓为中心；fitCars = 仓缺坐标，退回等小车位置到了按小车框一次视野 */
export type WhsFocus = { kind: 'center'; lng: number; lat: number } | { kind: 'fitCars' };

/**
 * 换仓时地图怎么重新定位。只在所选仓变化时返回动作：同一仓下的位置轮询、测试时钟步进、仓列表刷新都返回 null，
 * 用户拖动 / 缩放后不会被拉回；清空仓也返回 null，视野不动。sites 已滤掉缺坐标的仓
 */
export const whsFocusOnChange = (
    prevId: string | null,
    nextId: string | null,
    sites: WhsSite[],
): WhsFocus | null => {
    if (nextId === null || nextId === prevId) return null;
    const site = sites.find((s) => s.id === nextId);
    return site ? { kind: 'center', lng: site.longitude, lat: site.latitude } : { kind: 'fitCars' };
};

/**
 * 「归位」要框住的范围：全部小车 + 所选仓（PRD fitBtn）。没有小车返回 null，按 PRD 不动，不回落到飞向仓
 */
export const fitAllBounds = (
    cars: readonly { lng: number; lat: number }[],
    sites: readonly WhsSite[],
): GeoBounds | null => {
    if (cars.length === 0) return null;
    const points = [...cars, ...sites.map((s) => ({ lng: s.longitude, lat: s.latitude }))];
    return {
        west: Math.min(...points.map((p) => p.lng)),
        east: Math.max(...points.map((p) => p.lng)),
        south: Math.min(...points.map((p) => p.lat)),
        north: Math.max(...points.map((p) => p.lat)),
    };
};


/**
 * 谁戴皇冠：LEADING 桶里 (妥投率, 领取量) 最大的那辆车。
 *
 * 排序键与后端 `assignBuckets` / `driverComparator` 同源（rate DESC, allocated DESC）。
 * 二级键不能省——实际数据里 LEADING 桶大面积并列 100.00%，只比 rate 会退回数组顺序，
 * 而 `deliveryMonitorDriverPositions` 是不排序的（数仓行序）。
 *
 * 入参是当前地图上的车（含本轮没返回、变灰留着的），所以 rate 最高者若从没上报过位置，皇冠顺延给下一个。
 * rate 缺失或解析失败按 -1，排在所有正常值之后（LEADING 桶理论上不会出现，兜底不抛异常）。
 */
export const pickKingId = (
    positions: readonly DeliveryMonitorDriverPositionType[] | null | undefined,
): string | null => {
    const rateOf = (p: DeliveryMonitorDriverPositionType): number => {
        const n = Number(p.deliveryRate);
        return p.deliveryRate === null || Number.isNaN(n) ? -1 : n;
    };
    const leaders = (positions ?? []).filter((p) => p.bucket === 'LEADING');
    if (leaders.length === 0) return null;
    const king = leaders.reduce((best, p) => {
        const d = rateOf(p) - rateOf(best);
        if (d !== 0) return d > 0 ? p : best;
        // 并列保留先遇到的，与后端稳定排序一致
        return Number(p.allocatedParcels) > Number(best.allocatedParcels) ? p : best;
    });
    return king.driverAccountId;
};

export interface CarPresence {
    /** 本轮没返回、变灰留在原地的车 */
    lost: string[];
    /** 本轮没返回、直接删掉的车 */
    removed: string[];
}

/**
 * 每轮轮询后，地图上已有但本轮没返回的车怎么处理：位置是批量补传的、信号也常断，
 * 平时只变灰不删，等再返回时恢复；切换 HUB / WHS / 测试时刻后的首轮直接删，免得把上一个范围的车留成灰色。
 */
export const planCarPresence = (
    shownIds: Iterable<string>,
    returnedIds: ReadonlySet<string>,
    scopeChanged: boolean,
): CarPresence => {
    const missing = Array.from(shownIds).filter((id) => !returnedIds.has(id));
    return scopeChanged ? { lost: [], removed: missing } : { lost: missing, removed: [] };
};


/** 司机列表一次渲染/追加的条数。 */
export const DRIVER_LIST_BATCH = 20;

/** 距列表底部多少像素内算"滚到底"。约等于一行的高度，提前一行加载，滚动不卡顿。 */
export const DRIVER_LIST_SCROLL_THRESHOLD_PX = 80;

export interface ScrollMetrics {
    scrollTop: number;
    clientHeight: number;
    scrollHeight: number;
}

/**
 * 滚动加载：算出滚动后应该渲染多少条。
 *
 * 列表整份数据已在前端（后端不分页），这里只控制渲染条数——几百个司机节点一次性挂上去，
 * 大屏常驻不动会持续掉帧。到底才追加一批，不到底/已全部加载都原样返回，
 * 返回同一个数字可让调用方的 setState 命中 React 的 bail-out，不触发重渲染。
 */
export const nextVisibleCount = (
    current: number,
    total: number,
    metrics: ScrollMetrics,
    batch: number = DRIVER_LIST_BATCH,
): number => {
    if (current >= total) return current;
    const distanceToBottom = metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight;
    if (distanceToBottom > DRIVER_LIST_SCROLL_THRESHOLD_PX) return current;
    return Math.min(total, current + batch);
};

/**
 * 选 HUB / WHS：首选 id（来自左上角仓库树）在当前可选列表里就用它，否则（测试仓 / 非末端 HUB / 无权限 / 没指定）回退第一个。
 * 列表为空返回 undefined。
 */
export const pickSavedSiteId = (
    options: readonly { id: string }[],
    savedId: string | null,
): number | undefined => {
    const hit = savedId === null ? undefined : options.find((o) => o.id === savedId);
    const picked = hit ?? options[0];
    return picked === undefined ? undefined : Number(picked.id);
};

/**
 * 左上角仓库树选中的站点 → 大屏首选的 HUB / WHS id：树选 WHS 时 HUB 取它的上级、WHS 取它本身；
 * 树选 HUB 时 HUB 取它本身、WHS 不指定；没选中都为 null。
 */
export const treeSitePick = (
    site: { id: string; siteType?: number; parentSiteId?: string | number | null } | null,
): { hubId: string | null; whsId: string | null } => {
    if (site?.siteType === SiteType.HUB) return { hubId: String(site.id), whsId: null };
    if (site?.siteType === SiteType.WAREHOUSE) {
        return {
            hubId: site.parentSiteId == null ? null : String(site.parentSiteId),
            whsId: String(site.id),
        };
    }
    return { hubId: null, whsId: null };
};

/** 测试站点：sortingCode 或 name 去空格、转大写后以 TEST 开头，与后端大屏接口 isTestSite 同口径 */
export const isTestSite = (site: { name?: string | null; sortingCode?: string | null }): boolean =>
    [site.sortingCode, site.name].some((v) => (v ?? '').trim().toUpperCase().startsWith('TEST'));

/**
 * 树选中的是否为测试 HUB 下的仓（用于隐藏派送监控入口）：只看上级 HUB；
 * 上级 HUB 不在站点列表里（账号只有仓权限）时判断不了，按非测试处理。
 */
export const isUnderTestHub = (
    site: { siteType?: number; parentSiteId?: string | number | null } | null,
    sites: readonly { id: string; name?: string | null; sortingCode?: string | null }[],
): boolean => {
    if (site?.siteType !== SiteType.WAREHOUSE || site.parentSiteId == null) return false;
    const parentId = String(site.parentSiteId);
    const parent = sites.find((s) => String(s.id) === parentId);
    return parent !== undefined && isTestSite(parent);
};

/** HUB 下拉文案（按 PRD）：名称去掉末尾的「HUB」，如 LAX HUB → LAX；去掉后为空则用原名称 */
export const hubLabel = (site: { name: string }): string => {
    const name = site.name.trim();
    return name.replace(/\s*hub$/i, '') || name;
};

/** 任务日按美西日期（与其它数据看板一致）：传入时刻在美西的 YYYY-MM-DD */
export const pacificTaskDate = (ms: number): string => dayjs(ms).tz(DASHBOARD_PACIFIC_TZ).format('YYYY-MM-DD');

/** 美西任务日零点的 ISO 时刻，作为当日轨迹起点 */
export const pacificDayStartIso = (taskDate: string): string =>
    dayjs.tz(taskDate, DASHBOARD_PACIFIC_TZ).toISOString();
